import NewsCard from "@/components/NewsCard";
import SkeletonCard from "@/components/SkeletonCard";
import { ArrowCounterClockwise, WifiSlash } from "@phosphor-icons/react";

/**
 * Reusable infinite-scroll feed renderer.
 *
 * Renders the card grid, loading skeletons, error/retry UI, scroll sentinel,
 * and the end-of-feed "all caught up" message.
 *
 * Props:
 *   feed         — result object from useInfiniteFeed()
 *   sentinelRef  — ref from useScrollObserver(), attached to the scroll trigger
 *   heroFirst    — if true, the very first card uses the "hero" variant
 *   emptyMessage — text shown when there are no items at all
 *   testId       — data-testid prefix for the grid element
 */
export default function InfiniteNewsFeed({
  feed,
  sentinelRef,
  heroFirst = false,
  emptyMessage = "No stories found.",
  testId = "feed-grid",
}) {
  const {
    items,
    loading,
    loadingMore,
    hasMore,
    error,
    stale,
    retryInitial,
    retryMore,
  } = feed;

  return (
    <>
      {/* Stale-data notice */}
      {stale && (
        <div
          className="flex items-center gap-2 border-l-2 border-yellow-400 bg-yellow-50 px-4 py-2 text-xs text-yellow-700 mb-4 rounded-r"
          role="status"
          aria-live="polite"
        >
          <WifiSlash size={14} weight="bold" />
          Showing cached data — reconnect to refresh
        </div>
      )}

      {/*
        Main card grid.
        role="feed" + aria-busy lets screen readers announce live updates.
      */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4"
        data-testid={testId}
        role="feed"
        aria-busy={loading || loadingMore}
        aria-label="News feed"
      >
        {/* Initial skeleton placeholders */}
        {loading &&
          Array.from({ length: 12 }).map((_, i) => (
            <SkeletonCard key={`sk-init-${i}`} hero={heroFirst && i === 0} />
          ))}

        {/* Loaded cards — index % 12 keeps animation stagger snappy for every batch */}
        {!loading &&
          items.map((it, idx) => (
            <NewsCard
              key={it.id}
              item={it}
              index={idx % 12}
              variant={heroFirst && idx === 0 ? "hero" : "default"}
            />
          ))}

        {/* "Load more" skeleton placeholders appended inside the same grid */}
        {loadingMore &&
          Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={`sk-more-${i}`} />
          ))}
      </div>

      {/* Empty state */}
      {!loading && items.length === 0 && !error && (
        <div className="text-center py-20 text-slate-400 text-sm">
          {emptyMessage}
        </div>
      )}

      {/* Error / retry */}
      {error && !loading && (
        <div
          className="mt-6 border border-red-200 bg-red-50 p-6 text-center rounded-xl fade-up"
          role="alert"
          aria-live="assertive"
        >
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <button
            onClick={items.length === 0 ? retryInitial : retryMore}
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider border border-slate-200 px-5 py-2.5 hover:bg-slate-50 transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            aria-label="Retry loading news"
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            Retry
          </button>
        </div>
      )}

      {/*
        Scroll sentinel — the IntersectionObserver watches this element.
        It stays rendered as long as there are more items to load so the
        observer can keep firing as the user scrolls.
        Hidden when there is an error (sentinel re-appears after retry clears the error).
      */}
      {hasMore && !error && !loading && (
        <div
          ref={sentinelRef}
          aria-hidden="true"
          className="h-4 mt-4"
          data-testid="scroll-sentinel"
        />
      )}

      {/* End-of-feed message */}
      {!hasMore && !loading && items.length > 0 && (
        <div
          className="text-center py-12 border-t border-slate-100 mt-8 feed-end"
          aria-live="polite"
          aria-label="All stories loaded"
        >
          <span className="text-xs text-slate-400 uppercase tracking-widest">
            You're all caught up
          </span>
        </div>
      )}
    </>
  );
}
